import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './App.css';
import logger from './utils/logger';

/**
 * 슈퍼관리자 전용 통계 페이지.
 * 상단: 총 입금액 / 총 입금건수 / 총 출금액 / 총 출금건수 / 총 입금수수료 / 총 출금수수료
 * 필터: 전체 기간 또는 월별 조회
 * 하단: 조회 조건에 따른 분류별 합산 내역 (전체 기간 조회 시 월별 합산도 표시)
 */
function SuperDashboard() {
  const [month, setMonth] = useState(''); // '' = 전체 기간
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatNumber = (value) => new Intl.NumberFormat('ko-KR').format(Math.floor(Number(value) || 0));
  const formatCurrency = (value) => `${formatNumber(value)}원`;
  const formatCount = (value) => `${formatNumber(value)}건`;
  const formatMonthLabel = (value) => {
    if (!value) return '';
    const [y, m] = value.split('-');
    return `${y}년 ${Number(m)}월`;
  };

  const fetchOverview = useCallback(async (targetMonth) => {
    try {
      setLoading(true);
      const query = targetMonth ? `?month=${targetMonth}` : '';
      const response = await axios.get(`/api/super-stats/overview${query}`);
      setData(response.data);
      setError('');
    } catch (err) {
      logger.apiError('GET', '/api/super-stats/overview', err);
      const message = err?.response?.data?.error || '통계를 불러오지 못했습니다.';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview(month);
  }, [month, fetchOverview]);

  const summary = data?.summary || {};
  const byCompany = Array.isArray(data?.byCompany) ? data.byCompany : [];
  const byMonth = Array.isArray(data?.byMonth) ? data.byMonth : [];
  const availableMonths = Array.isArray(data?.availableMonths) ? data.availableMonths : [];
  const isAllPeriod = !month;

  const periodLabel = isAllPeriod
    ? (summary.first_date && summary.last_date
      ? `전체 기간 (${summary.first_date} ~ ${summary.last_date})`
      : '전체 기간')
    : `${formatMonthLabel(month)} (${data?.period?.start || ''} ~ ${data?.period?.end || ''})`;

  const cards = [
    { key: 'deposit_amount', label: '총 입금액', value: formatCurrency(summary.deposit_amount), tone: 'amount' },
    { key: 'deposit_count', label: '총 입금건수', value: formatCount(summary.deposit_count), tone: 'count' },
    { key: 'withdrawal_amount', label: '총 출금액', value: formatCurrency(summary.withdrawal_amount), tone: 'withdraw' },
    { key: 'withdrawal_count', label: '총 출금건수', value: formatCount(summary.withdrawal_count), tone: 'count' },
    { key: 'deposit_fee', label: '총 입금수수료', value: formatCurrency(summary.deposit_fee), tone: 'fee' },
    { key: 'withdrawal_fee', label: '총 출금수수료', value: formatCurrency(summary.withdrawal_fee), tone: 'fee' }
  ];

  const renderSumRow = (row) => (
    <>
      <td className="super-stats-num">{formatCurrency(row.deposit_amount)}</td>
      <td className="super-stats-num">{formatCount(row.deposit_count)}</td>
      <td className="super-stats-num">{formatCurrency(row.withdrawal_amount)}</td>
      <td className="super-stats-num">{formatCount(row.withdrawal_count)}</td>
      <td className="super-stats-num">{formatCurrency(row.deposit_fee)}</td>
      <td className="super-stats-num">{formatCurrency(row.withdrawal_fee)}</td>
      <td className="super-stats-num super-stats-total">{formatCurrency(row.deposit_fee + row.withdrawal_fee)}</td>
      <td className="super-stats-num">{formatNumber(row.input_count)}일</td>
    </>
  );

  const sumHeaders = (
    <>
      <th>총 입금액</th>
      <th>총 입금건수</th>
      <th>총 출금액</th>
      <th>총 출금건수</th>
      <th>총 입금수수료</th>
      <th>총 출금수수료</th>
      <th>수수료 합계</th>
      <th>입력 일수</th>
    </>
  );

  return (
    <div className="deposit-table-container super-stats-page">
      <div className="super-stats-header">
        <div>
          <h2>전체 통계</h2>
          <p className="super-stats-subtitle">
            슈퍼관리자 전용 · {periodLabel}
          </p>
        </div>
        <div className="super-stats-filter">
          <label htmlFor="super-stats-month">조회 기간</label>
          <select
            id="super-stats-month"
            className="reserve-filter-input"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            disabled={loading && !data}
          >
            <option value="">전체 기간</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>{formatMonthLabel(m)}</option>
            ))}
            {month && !availableMonths.includes(month) && (
              <option value={month}>{formatMonthLabel(month)}</option>
            )}
          </select>
          <input
            type="month"
            className="reserve-filter-input"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            aria-label="월 직접 선택"
          />
          {!isAllPeriod && (
            <button type="button" className="reserve-filter-reset" onClick={() => setMonth('')}>
              전체 보기
            </button>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="super-stats-cards">
        {cards.map((card) => (
          <div key={card.key} className={`super-stats-card super-stats-card--${card.tone}`}>
            <div className="super-stats-card-label">{card.label}</div>
            <div className="super-stats-card-value">{loading && !data ? '...' : card.value}</div>
          </div>
        ))}
      </div>
      <p className="super-stats-note">
        수수료는 본사 건당 수수료(입금 {formatNumber(440)}원 · 출금 {formatNumber(990)}원 기본값, 정산 입력값 우선) × 건수 기준입니다.
      </p>

      <section className="super-stats-section">
        <h3>분류별 합산 내역</h3>
        {loading && !data ? (
          <div className="super-stats-empty">불러오는 중...</div>
        ) : byCompany.length === 0 ? (
          <div className="super-stats-empty">조회 기간에 정산 입력 내역이 없습니다.</div>
        ) : (
          <div className="super-stats-table-wrap">
            <table className="deposit-table super-stats-table">
              <thead>
                <tr>
                  <th>분류</th>
                  {sumHeaders}
                </tr>
              </thead>
              <tbody>
                {byCompany.map((row) => (
                  <tr key={row.company}>
                    <td className="super-stats-name">{row.company}</td>
                    {renderSumRow(row)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="reserve-summary-row">
                  <td>합계</td>
                  {renderSumRow(summary)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {isAllPeriod && byMonth.length > 0 && (
        <section className="super-stats-section">
          <h3>월별 합산 내역</h3>
          <div className="super-stats-table-wrap">
            <table className="deposit-table super-stats-table">
              <thead>
                <tr>
                  <th>월</th>
                  {sumHeaders}
                </tr>
              </thead>
              <tbody>
                {byMonth.map((row) => (
                  <tr key={row.month}>
                    <td className="super-stats-name">
                      <button
                        type="button"
                        className="super-stats-month-link"
                        onClick={() => setMonth(row.month)}
                        title="이 월만 조회"
                      >
                        {formatMonthLabel(row.month)}
                      </button>
                    </td>
                    {renderSumRow(row)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default SuperDashboard;
